import React, { useState, useEffect, useRef } from 'react';
import api from '../api/axios';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext';


const SouthIndianChart = ({ kundali }) => {
  const getPlanetsForHouse = (houseNumber) => {
    const code = (kundali.name || '').charCodeAt(0) + houseNumber;
    const planets = [];
    if (code % 12 === 0) planets.push('Su');
    if ((code + 3) % 12 === 0) planets.push('Mo');
    if ((code + 7) % 12 === 0) planets.push('Ma');
    if ((code + 2) % 12 === 0) planets.push('Me');
    if ((code + 9) % 12 === 0) planets.push('Ju');
    if ((code + 5) % 12 === 0) planets.push('Ve');
    if ((code + 11) % 12 === 0) planets.push('Sa');
    if ((code + 4) % 12 === 0) planets.push('Ra');
    if ((code + 10) % 12 === 0) planets.push('Ke');
    return planets.join(' ');
  };

  const houseLabels = [
    { num: 12, name: 'Pis', row: 1, col: 1 },
    { num: 1, name: 'Ari', row: 1, col: 2, isLagna: true },
    { num: 2, name: 'Tau', row: 1, col: 3 },
    { num: 3, name: 'Gem', row: 1, col: 4 },
    { num: 4, name: 'Can', row: 2, col: 4 },
    { num: 5, name: 'Leo', row: 3, col: 4 },
    { num: 6, name: 'Vir', row: 4, col: 4 },
    { num: 7, name: 'Lib', row: 4, col: 3 },
    { num: 8, name: 'Sco', row: 4, col: 2 },
    { num: 9, name: 'Sag', row: 4, col: 1 },
    { num: 10, name: 'Cap', row: 3, col: 1 },
    { num: 11, name: 'Aqu', row: 2, col: 1 }
  ];

  return (
    <div className="w-full max-w-[220px] aspect-square grid grid-cols-4 grid-rows-4 gap-0.5 bg-[#E8B84B]/20 border border-[#E8B84B]/40 p-0.5 rounded-xl mx-auto my-3 text-[8px] bg-black">
      {houseLabels.map((h) => {
        const planets = getPlanetsForHouse(h.num);
        return (
          <div
            key={h.num}
            style={{ gridRow: h.row, gridColumn: h.col }}
            className={`bg-[#0E0E26] border border-[#E8B84B]/10 flex flex-col justify-between p-0.5 text-center min-h-[42px] ${h.isLagna ? 'bg-[#E8B84B]/5 font-black text-[#E8B84B]' : 'text-stone-400'}`}
          >
            <div className="flex justify-between items-center text-[7px] opacity-60">
              <span>{h.name}</span>
              <span>{h.num}</span>
            </div>
            <div className="text-white font-bold text-center text-[9px] tracking-tight">{planets || '—'}</div>
            <div className="text-[6px] text-stone-600">{h.isLagna ? 'Lagna' : ''}</div>
          </div>
        );
      })}
      <div
        style={{ gridRow: '2 / 4', gridColumn: '2 / 4' }}
        className="bg-[#14122B] border border-[#E8B84B]/20 flex flex-col items-center justify-center p-1 text-center text-[7px]"
      >
        <span className="text-[#E8B84B] font-bold uppercase tracking-wider block">Kundali</span>
        <span className="text-stone-400 text-[6px] mt-0.5 max-w-[70px] truncate">{kundali.name}</span>
        <span className="text-stone-600 text-[5px]">{kundali.dob}</span>
      </div>
    </div>
  );
};

const ConsultationInbox = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [isPartyTyping, setIsPartyTyping] = useState(false);
  const [isPartyOnline, setIsPartyOnline] = useState(false);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Custom Toast State
  const [toastMessage, setToastMessage] = useState(null);

  const showToast = (text, type = 'info') => {
    setToastMessage({ text, type });
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  const toast = {
    success: (msg) => showToast(msg, 'success'),
    error: (msg) => showToast(msg, 'error'),
    info: (msg) => showToast(msg, 'info')
  };

  // Kundali / Chart States
  const [activeTab, setActiveTab] = useState('info'); // 'info' | 'kundali'
  // eslint-disable-next-line no-unused-vars
  const [kundalis, setKundalis] = useState([]);
  const [selectedKundali, setSelectedKundali] = useState(null);
  const [showKundaliForm, setShowKundaliForm] = useState(false);
  const [submittingKundali, setSubmittingKundali] = useState(false);
  const [kundaliForm, setKundaliForm] = useState({
    name: '',
    dob: '',
    tob: '',
    pob: '',
    gender: 'Male',
    notes: ''
  });

  // WebRTC States & Refs
  const [incomingCall, setIncomingCall] = useState(null);
  const [callActive, setCallActive] = useState(false);
  const [callType, setCallType] = useState('chat'); // 'chat', 'audio', 'video'
  const [connectionStatus, setConnectionStatus] = useState('connecting'); // 'connecting' | 'connected' | 'failed' | 'disconnected'
  const [remoteStreamURL, setRemoteStreamURL] = useState(null);
  const [localStreamURL, setLocalStreamURL] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [isCalling, setIsCalling] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isCamOff, setIsCamOff] = useState(false);
  const peerConnection = useRef(null);
  const localStream = useRef(null);
  const remoteStream = useRef(null);
  const remoteAudioRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);
  const iceCandidatesQueue = useRef([]);
  const inputRef = useRef(null);

  // Bind local stream
  useEffect(() => {
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = localStreamURL;
    }
  }, [localStreamURL]);

  // Bind remote streams
  useEffect(() => {
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = remoteStreamURL;
    }
    if (callType === 'video' && remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStreamURL;
    }
  }, [remoteStreamURL, callType]);

  const selectedSessionRef = useRef(null);

  useEffect(() => {
    selectedSessionRef.current = selectedSession;
  }, [selectedSession]);

  useEffect(() => {
    fetchSessions();

    // Initialize socket once
    const token = localStorage.getItem('amps_token');
    const socket = io(process.env.REACT_APP_CONSULTATION_SOCKET_URL || 'http://localhost:4000/consultation', {
      auth: { token }
    });
    socketRef.current = socket;

    socket.on('new_consultation_message', (msg) => {
      const currentSession = selectedSessionRef.current;
      if (currentSession &&
        msg.userId === currentSession.userId &&
        msg.acaryaId === currentSession.acaryaId) {
        setMessages(prev => {
          const exists = prev.find(m => m._id === msg._id);
          if (exists) return prev;
          return [...prev, msg];
        });
        setIsPartyTyping(false);
        scrollToBottom();
      }
      fetchSessions();
    });

    socket.on('party_typing', (data) => {
      const currentSession = selectedSessionRef.current;
      if (currentSession && data.role === 'USER') {
        setIsPartyTyping(data.typing);
      }
    });

    socket.on('party_online', (data) => {
      const currentSession = selectedSessionRef.current;
      if (currentSession && data.role === 'USER' && data.userId === currentSession.userId) {
        setIsPartyOnline(true);
      }
    });

    socket.on('party_offline', (data) => {
      const currentSession = selectedSessionRef.current;
      if (currentSession && data.role === 'USER' && data.userId === currentSession.userId) {
        setIsPartyOnline(false);
        setIsPartyTyping(false);
      }
    });

    socket.on('incoming_call', (data) => {
      console.log('📞 Incoming call:', data);
      setIncomingCall(data);
    });

    socket.on('call_accepted', async (data) => {
      console.log('✅ Call accepted');
      setConnectionStatus('connecting');
      setCallActive(true);

      try {
        const pc = initPeerConnection();
        if (localStream.current) {
          localStream.current.getTracks().forEach(track => pc.addTrack(track, localStream.current));
        }
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const current = selectedSessionRef.current;
        socket.emit('webrtc_signal', {
          acaryaId: current.acaryaId,
          userId: current.userId,
          signal: offer
        });
        console.log('📡 Acarya sent offer after user accepted');
      } catch (err) {
        console.error('Error creating acarya offer:', err);
      }
    });

    socket.on('call_rejected', () => {
      console.log('❌ Call rejected');
      cleanupCall();
    });

    socket.on('call_ended', () => {
      console.log('🏁 Call ended');
      cleanupCall();
    });

    socket.on('webrtc_signal', async ({ signal, from }) => {
      console.log('📡 Received WebRTC signal type:', signal.type || 'candidate');
      try {
        const current = selectedSessionRef.current;
        if (signal.type === 'offer') {
          let pc = peerConnection.current;
          if (!pc) {
            pc = initPeerConnection();
          }
          await pc.setRemoteDescription(new RTCSessionDescription(signal));
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          socket.emit('webrtc_signal', {
            acaryaId: current.acaryaId,
            userId: current.userId,
            signal: answer
          });

          // Process queued candidates
          while (iceCandidatesQueue.current.length > 0) {
            const cand = iceCandidatesQueue.current.shift();
            await pc.addIceCandidate(cand).catch(e => console.error("Error adding queued candidate:", e));
          }
        } else if (signal.type === 'answer') {
          if (peerConnection.current) {
            await peerConnection.current.setRemoteDescription(new RTCSessionDescription(signal));

            // Process queued candidates
            while (iceCandidatesQueue.current.length > 0) {
              const cand = iceCandidatesQueue.current.shift();
              await peerConnection.current.addIceCandidate(cand).catch(e => console.error("Error adding queued candidate:", e));
            }
          }
        } else if (signal.candidate) {
          const cand = new RTCIceCandidate(signal);
          if (peerConnection.current && peerConnection.current.remoteDescription && peerConnection.current.remoteDescription.type) {
            await peerConnection.current.addIceCandidate(cand).catch(e => console.error("Error adding candidate:", e));
          } else {
            iceCandidatesQueue.current.push(cand);
          }
        }
      } catch (err) {
        console.error('WebRTC signal error:', err);
      }
    });

    return () => {
      if (socket) socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only once on mount

  const fetchSessions = async () => {
    try {
      const res = await api.get('/consultations/sessions');
      setSessions(res.data.data);
      setLoading(false);
    } catch (err) {
      console.error("Failed to fetch sessions", err);
      setLoading(false);
    }
  };

  const fetchKundalis = async (userId) => {
    try {
      const res = await api.get('/acaryas/me/kundalis');
      if (res.data.success) {
        // filter by userId
        const userK = res.data.data.filter(k => k.userId === userId);
        setKundalis(userK);
        if (userK.length > 0) {
          setSelectedKundali(userK[0]);
        } else {
          setSelectedKundali(null);
        }
      }
    } catch (err) {
      console.error("Failed to fetch Kundalis", err);
    }
  };

  const handleCreateKundali = async (e) => {
    e.preventDefault();
    if (!selectedSession) return;
    setSubmittingKundali(true);
    try {
      const payload = {
        ...kundaliForm,
        userId: selectedSession.userId,
        acaryaId: selectedSession.acaryaId
      };
      const res = await api.post('/acaryas/me/kundalis', payload);
      if (res.data.success) {
        setKundalis(prev => [res.data.data, ...prev]);
        setSelectedKundali(res.data.data);
        setShowKundaliForm(false);
        setKundaliForm({ name: '', dob: '', tob: '', pob: '', gender: 'Male', notes: '' });
      }
    } catch (err) {
      console.error("Failed to create Kundali", err);
    } finally {
      setSubmittingKundali(false);
    }
  };

  const selectSession = async (session) => {
    setSelectedSession(session);
    setMessages([]);
    setIsPartyTyping(false);
    setIsPartyOnline(false);
    setShowKundaliForm(false);
    setActiveTab('info');
    try {
      const res = await api.get(`/consultations/history/${session.acaryaId}?userId=${session.userId}`);
      setMessages(res.data.data);

      if (socketRef.current) {
        socketRef.current.emit('join_consultation', {
          acaryaId: session.acaryaId,
          userId: session.userId
        });
      }

      scrollToBottom();
      fetchKundalis(session.userId);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } catch (err) {
      console.error("Failed to fetch chat history", err);
    }
  };

  const sendMessage = (e) => {
    if (e) e.preventDefault();
    if (!messageInput.trim() || !selectedSession) return;

    const payload = {
      acaryaId: selectedSession.acaryaId,
      userId: selectedSession.userId,
      message: messageInput.trim()
    };

    socketRef.current.emit('send_consultation_message', payload);
    socketRef.current.emit('stop_typing', { acaryaId: selectedSession.acaryaId, userId: selectedSession.userId });
    setMessageInput('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  const handleInputChange = (e) => {
    setMessageInput(e.target.value);

    if (socketRef.current && selectedSession) {
      socketRef.current.emit('typing', { acaryaId: selectedSession.acaryaId, userId: selectedSession.userId });

      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

      typingTimeoutRef.current = setTimeout(() => {
        socketRef.current.emit('stop_typing', { acaryaId: selectedSession.acaryaId, userId: selectedSession.userId });
      }, 2000);
    }
  };

  const formatMessageDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString([], { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // WebRTC Functions
  const initPeerConnection = () => {
    if (peerConnection.current) peerConnection.current.close();
    iceCandidatesQueue.current = [];

    const pc = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        {
          urls: 'turn:openrelay.metered.ca:80',
          username: 'openrelayproject',
          credential: 'openrelayproject'
        },
        {
          urls: 'turn:openrelay.metered.ca:443',
          username: 'openrelayproject',
          credential: 'openrelayproject'
        },
        {
          urls: 'turn:openrelay.metered.ca:443?transport=tcp',
          username: 'openrelayproject',
          credential: 'openrelayproject'
        }
      ]
    });

    pc.onicecandidate = (event) => {
      if (event.candidate && selectedSessionRef.current) {
        socketRef.current.emit('webrtc_signal', {
          acaryaId: selectedSessionRef.current.acaryaId,
          userId: selectedSessionRef.current.userId,
          signal: event.candidate.toJSON()
        });
      }
    };

    pc.onconnectionstatechange = () => {
      console.log('⚡ WebRTC connectionState:', pc.connectionState);
      setConnectionStatus(pc.connectionState);
      if (pc.connectionState === 'connected') {
        toast.success('Divine connection established!');
      } else if (pc.connectionState === 'failed') {
        toast.error('Connection failed. Please check your network.');
      } else if (pc.connectionState === 'disconnected') {
        toast.info('Call disconnected.');
      }
    };

    pc.oniceconnectionstatechange = () => {
      console.log('⚡ WebRTC iceConnectionState:', pc.iceConnectionState);
      if (pc.iceConnectionState === 'connected') {
        setConnectionStatus('connected');
      } else if (pc.iceConnectionState === 'failed') {
        setConnectionStatus('failed');
      } else if (pc.iceConnectionState === 'disconnected') {
        setConnectionStatus('disconnected');
      }
    };

    pc.ontrack = (event) => {
      console.log('🎬 Acarya received remote track:', event.track.kind);
      if (!remoteStream.current) {
        remoteStream.current = new MediaStream();
      }
      remoteStream.current.addTrack(event.track);
      setRemoteStreamURL(new MediaStream(remoteStream.current));
    };

    peerConnection.current = pc;
    return pc;
  };

  const acceptCall = async () => {
    if (!incomingCall || !selectedSession) return;

    try {
      console.log('✅ Acarya accepting incoming call...');
      setConnectionStatus('connecting');
      const type = incomingCall.type;
      setCallType(type);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: type === 'video',
        audio: true
      });

      localStream.current = stream;
      setLocalStreamURL(new MediaStream(stream));

      const pc = initPeerConnection();
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      socketRef.current.emit('accept_call', {
        to: incomingCall.from,
        acaryaId: selectedSession.acaryaId,
        userId: selectedSession.userId,
        type
      });

      setCallActive(true);
      setIncomingCall(null);
    } catch (err) {
      console.error('Failed to accept call:', err);
    }
  };

  const startCall = async (type) => {
    if (!selectedSession) return;

    try {
      console.log(`🚀 Acarya starting ${type} call...`);
      setConnectionStatus('connecting');
      setCallType(type);
      setIsCalling(true);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: type === 'video',
        audio: true
      });

      localStream.current = stream;
      setLocalStreamURL(new MediaStream(stream));

      socketRef.current.emit('start_call', {
        acaryaId: selectedSession.acaryaId,
        userId: selectedSession.userId,
        type
      });

      setCallActive(true);
    } catch (err) {
      console.error('Failed to start call:', err);
    }
  };

  const rejectCall = () => {
    if (!incomingCall || !selectedSession) return;
    socketRef.current.emit('reject_call', {
      to: incomingCall.from,
      acaryaId: selectedSession.acaryaId,
      userId: selectedSession.userId
    });
    setIncomingCall(null);
  };

  const endCall = () => {
    if (selectedSession) {
      socketRef.current.emit('end_call', {
        acaryaId: selectedSession.acaryaId,
        userId: selectedSession.userId
      });
    }
    cleanupCall();
  };

  const toggleMute = () => {
    if (localStream.current) {
      localStream.current.getAudioTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleCamera = () => {
    if (localStream.current) {
      localStream.current.getVideoTracks().forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsCamOff(!isCamOff);
    }
  };

  const cleanupCall = () => {
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => track.stop());
      localStream.current = null;
    }
    if (remoteStream.current) {
      remoteStream.current.getTracks().forEach(track => track.stop());
      remoteStream.current = null;
    }
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    iceCandidatesQueue.current = [];
    setCallActive(false);
    setIncomingCall(null);
    setLocalStreamURL(null);
    setRemoteStreamURL(null);
    setIsCalling(false);
    setIsMuted(false);
    setIsCamOff(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0E0E26] flex items-center justify-center">
      <div className="w-12 h-12 border-4 border-[#E8B84B]/20 border-t-[#E8B84B] rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="h-[calc(100vh-120px)] flex bg-[#0E0E26] rounded-3xl overflow-hidden border border-white/5 font-sans shadow-2xl">
      {/* Dedicated Audio Element for Remote Stream (Always mounted to ensure reliable WebRTC audio track binding) */}
      <audio
        autoPlay
        playsInline
        ref={remoteAudioRef}
      />
      {/* Session List */}
      <div className="w-[350px] border-r border-white/10 bg-[#0E0E26] flex flex-col">
        <div className="p-5 bg-[#14122B] flex items-center justify-between border-b border-white/5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-stone-800 border border-[#E8B84B]/30 flex items-center justify-center overflow-hidden">
              <span className="material-symbols-outlined text-[#E8B84B]">psychology</span>
            </div>
            <h2 className="text-white font-bold text-base tracking-wide">Conversations</h2>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {sessions.length === 0 ? (
            <div className="p-10 text-center text-white/20 text-sm">No active consultations.</div>
          ) : (
            sessions.map((sess) => (
              <button
                key={`${sess.userId}-${sess.acaryaId}`}
                onClick={() => selectSession(sess)}
                className={`w-full p-4 flex gap-3 hover:bg-white/5 transition-all border-b border-white/5 text-left ${selectedSession?.userId === sess.userId && selectedSession?.acaryaId === sess.acaryaId ? 'bg-white/5 border-l-4 border-[#E8B84B]' : ''}`}
              >
                <div className="w-12 h-12 rounded-full bg-stone-800 flex items-center justify-center shrink-0 border border-white/5 overflow-hidden">
                  <span className="material-symbols-outlined text-white/10 text-3xl">account_circle</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-center mb-1">
                    <h4 className="text-white font-bold truncate text-sm">{sess.userName}</h4>
                    <span className="text-[10px] text-white/20 font-black uppercase">
                      {new Date(sess.lastTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="text-xs text-white/40 truncate flex-1 leading-tight">
                      {sess.lastMessage}
                    </p>
                    <span className="bg-[#2A2858] text-white text-[9px] px-2 py-0.5 rounded-full font-black ml-2 border border-white/10">
                      {sess.remainingMinutes}M
                    </span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area & Detail Panel */}
      <div className="flex-1 flex bg-[#0E0E26]">
        {selectedSession ? (
          <>
            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col min-w-0 border-r border-white/5">
              {/* Chat Header */}
              <div className="p-4 bg-[#14122B] flex items-center justify-between shadow-xl z-10 border-b border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-stone-800 border border-[#E8B84B]/30 flex items-center justify-center">
                    <span className="material-symbols-outlined text-[#E8B84B]">person</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm tracking-wide leading-tight">{selectedSession.userName}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className={`w-1.5 h-1.5 rounded-full ${isPartyOnline ? 'bg-green-500 animate-pulse' : 'bg-stone-500'}`}></div>
                      <p className="text-[10px] text-white/40 font-black uppercase tracking-widest">
                        {isPartyOnline ? 'Online Now' : 'Last seen ' + new Date(selectedSession.lastTimestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => startCall('audio')}
                    className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-[#E8B84B] hover:text-black transition-all"
                  >
                    <span className="material-symbols-outlined text-[20px]">call</span>
                  </button>
                  <button
                    onClick={() => startCall('video')}
                    className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-[#E8B84B] hover:text-black transition-all"
                  >
                    <span className="material-symbols-outlined text-[20px]">videocam</span>
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-8 space-y-4 relative custom-scrollbar bg-[#0E0E26]">
                {messages.map((msg, idx) => {
                  const isMe = msg.senderRole === 'ACARYA' || msg.senderId === user.id;
                  const showDate = idx === 0 || formatMessageDate(messages[idx - 1].createdAt) !== formatMessageDate(msg.createdAt);

                  return (
                    <React.Fragment key={msg._id || idx}>
                      {showDate && (
                        <div className="flex justify-center my-8">
                          <span className="bg-white/5 text-white/20 text-[9px] px-4 py-1 rounded-full font-black uppercase tracking-widest border border-white/5">
                            {formatMessageDate(msg.createdAt)}
                          </span>
                        </div>
                      )}
                      <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                        <div className={`relative min-w-[100px] max-w-[75%] px-4 py-3 shadow-2xl ${isMe
                          ? 'bg-[#2A2858] text-white rounded-2xl rounded-tr-none border border-white/10'
                          : 'bg-[#14122B] text-white rounded-2xl rounded-tl-none border border-white/5'
                          }`}>
                          <p className="text-[14px] leading-relaxed mb-1">{msg.message}</p>
                          <div className="flex items-center justify-end gap-1.5 opacity-40">
                            <span className="text-[9px] font-black uppercase tracking-tighter">
                              {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            {isMe && (
                              <span className="material-symbols-outlined text-[14px]">done_all</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}

                {isPartyTyping && (
                  <div className="flex justify-start">
                    <div className="bg-[#14122B]/50 text-[#E8B84B] px-4 py-2 rounded-full text-[10px] font-bold italic animate-pulse border border-[#E8B84B]/10 flex items-center gap-2">
                      <div className="flex gap-1">
                        <span className="w-1 h-1 bg-[#E8B84B] rounded-full animate-bounce"></span>
                        <span className="w-1 h-1 bg-[#E8B84B] rounded-full animate-bounce delay-75"></span>
                        <span className="w-1 h-1 bg-[#E8B84B] rounded-full animate-bounce delay-150"></span>
                      </div>
                      {selectedSession.userName} is typing...
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-4 bg-[#14122B] border-t border-white/5">
                <form onSubmit={sendMessage} className="max-w-5xl mx-auto flex gap-3">
                  <input
                    ref={inputRef}
                    type="text"
                    value={messageInput}
                    onChange={handleInputChange}
                    placeholder="Type your response..."
                    className="flex-1 bg-black/20 border border-white/10 rounded-2xl px-5 py-4 text-white text-sm focus:outline-none focus:ring-0 focus:border-[#E8B84B]/50 transition-all placeholder-white/10"
                  />
                  <button
                    type="submit"
                    disabled={!messageInput.trim()}
                    className="w-14 h-14 bg-[#E8B84B] text-black rounded-2xl flex items-center justify-center hover:bg-white active:scale-95 transition-all shadow-xl disabled:opacity-20 disabled:grayscale"
                  >
                    <span className="material-symbols-outlined font-bold">send</span>
                  </button>
                </form>
              </div>
            </div>

            {/* Right Side - Subscriber Panel */}
            <div className="w-80 flex flex-col p-6 items-center text-center bg-[#14122B]/30 border-l border-white/5 overflow-y-auto custom-scrollbar">
              <div className="w-20 h-20 rounded-full bg-stone-800 border-2 border-[#E8B84B]/30 flex items-center justify-center mb-3 overflow-hidden shadow-2xl relative shrink-0">
                <span className="material-symbols-outlined text-white/10 text-5xl">account_circle</span>
                <div className={`absolute bottom-0 right-0 w-3 h-3 border-2 border-[#0E0E26] rounded-full ${isPartyOnline ? 'bg-green-500' : 'bg-stone-500'}`}></div>
              </div>
              <h3 className="text-base font-bold text-white mb-0.5 truncate w-full">{selectedSession.userName}</h3>
              <p className="text-[#E8B84B] text-[9px] font-black uppercase tracking-widest mb-4 truncate w-full">{selectedSession.userEmail}</p>

              {/* Right side tab selection */}
              <div className="flex w-full bg-black/20 rounded-xl p-1 mb-4 border border-white/5">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${activeTab === 'info' ? 'bg-[#E8B84B]/15 text-[#E8B84B] border border-[#E8B84B]/20' : 'text-stone-500 hover:text-stone-300'}`}
                >
                  Seeker Info
                </button>
                <button
                  onClick={() => setActiveTab('kundali')}
                  className={`flex-1 py-2 text-[10px] font-bold uppercase tracking-wider rounded-lg transition-all ${activeTab === 'kundali' ? 'bg-[#E8B84B]/15 text-[#E8B84B] border border-[#E8B84B]/20' : 'text-stone-500 hover:text-stone-300'}`}
                >
                  🔮 Kundali
                </button>
              </div>

              {activeTab === 'info' ? (
                <div className="w-full space-y-3">
                  <div className="bg-black/20 p-4 rounded-2xl border border-white/5 text-left">
                    <p className="text-[10px] text-white/30 uppercase font-black tracking-widest mb-1">Current Balance</p>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold text-base">{selectedSession.remainingMinutes}m</span>
                      <span className="material-symbols-outlined text-[#E8B84B] text-[20px]">timer</span>
                    </div>
                  </div>

                  <div className="bg-black/20 p-4 rounded-2xl border border-white/5 text-left">
                    <p className="text-[10px] text-white/30 uppercase font-black tracking-widest mb-1">Subscription</p>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold">{selectedSession.userPlan}</span>
                      <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${selectedSession.userSubscriptionStatus === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                        {selectedSession.userSubscriptionStatus}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full text-left">
                  {showKundaliForm ? (
                    <form onSubmit={handleCreateKundali} className="bg-black/30 p-4 border border-[#E8B84B]/10 rounded-2xl space-y-3">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[9px] font-black uppercase text-[#E8B84B] tracking-wider">Input Birth Details</span>
                        <button
                          type="button"
                          onClick={() => setShowKundaliForm(false)}
                          className="text-[9px] font-bold text-red-400 hover:underline"
                        >
                          Cancel
                        </button>
                      </div>

                      <div>
                        <label className="block text-[8px] text-stone-500 font-bold uppercase mb-0.5">Full Name</label>
                        <input
                          type="text"
                          required
                          value={kundaliForm.name}
                          onChange={e => setKundaliForm({ ...kundaliForm, name: e.target.value })}
                          className="w-full bg-[#1c1917] border border-white/10 rounded-lg p-2 text-xs text-stone-200 outline-none"
                          placeholder="e.g. Ramesh Kumar"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[8px] text-stone-500 font-bold uppercase mb-0.5">Date of Birth</label>
                          <input
                            type="date"
                            required
                            value={kundaliForm.dob}
                            onChange={e => setKundaliForm({ ...kundaliForm, dob: e.target.value })}
                            className="w-full bg-[#1c1917] border border-white/10 rounded-lg p-2 text-xs text-stone-200 outline-none"
                          />
                        </div>
                        <div>
                          <label className="block text-[8px] text-stone-500 font-bold uppercase mb-0.5">Time of Birth</label>
                          <input
                            type="text"
                            required
                            value={kundaliForm.tob}
                            onChange={e => setKundaliForm({ ...kundaliForm, tob: e.target.value })}
                            className="w-full bg-[#1c1917] border border-white/10 rounded-lg p-2 text-xs text-stone-200 outline-none"
                            placeholder="e.g. 08:30 AM"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[8px] text-stone-500 font-bold uppercase mb-0.5">Place of Birth</label>
                        <input
                          type="text"
                          required
                          value={kundaliForm.pob}
                          onChange={e => setKundaliForm({ ...kundaliForm, pob: e.target.value })}
                          className="w-full bg-[#1c1917] border border-white/10 rounded-lg p-2 text-xs text-stone-200 outline-none"
                          placeholder="e.g. New Delhi, India"
                        />
                      </div>

                      <div>
                        <label className="block text-[8px] text-stone-500 font-bold uppercase mb-0.5">Gender</label>
                        <select
                          value={kundaliForm.gender}
                          onChange={e => setKundaliForm({ ...kundaliForm, gender: e.target.value })}
                          className="w-full bg-[#1c1917] border border-white/10 rounded-lg p-2 text-xs text-stone-200 outline-none"
                        >
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <button
                        type="submit"
                        disabled={submittingKundali}
                        className="w-full py-2 bg-[#E8B84B] text-black font-black text-[10px] uppercase tracking-wider rounded-xl hover:bg-white active:scale-95 transition-all"
                      >
                        {submittingKundali ? 'Calculating...' : '🔮 Generate Chart'}
                      </button>
                    </form>
                  ) : selectedKundali ? (
                    <div className="space-y-4">
                      {/* Birth metadata card */}
                      <div className="bg-black/20 p-4 border border-white/5 rounded-2xl space-y-1.5 text-xs text-stone-300">
                        <div className="flex justify-between">
                          <span className="text-stone-500">Name:</span>
                          <span className="font-bold text-white">{selectedKundali.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-500">Date:</span>
                          <span className="font-bold">{selectedKundali.dob}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-500">Time:</span>
                          <span className="font-bold">{selectedKundali.tob}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-stone-500">Place:</span>
                          <span className="font-bold truncate max-w-[120px]">{selectedKundali.pob}</span>
                        </div>
                      </div>

                      {/* Interactive Vedic Chart */}
                      <SouthIndianChart kundali={selectedKundali} />

                      <button
                        onClick={() => {
                          setShowKundaliForm(true);
                          setKundaliForm({
                            name: selectedSession.userName,
                            dob: '',
                            tob: '',
                            pob: '',
                            gender: 'Male',
                            notes: ''
                          });
                        }}
                        className="w-full py-2 bg-white/5 border border-white/10 text-stone-400 text-center font-bold text-[9px] uppercase tracking-wider rounded-xl hover:bg-white/10 hover:text-white transition-all"
                      >
                        Change Birth Details
                      </button>
                    </div>
                  ) : (
                    <div className="p-6 text-center bg-black/10 border border-dashed border-white/5 rounded-2xl">
                      <p className="text-[10px] text-stone-500 font-bold uppercase tracking-wider mb-3 leading-relaxed">
                        No birth details submitted by user.
                      </p>
                      <button
                        onClick={() => {
                          setShowKundaliForm(true);
                          setKundaliForm({
                            name: selectedSession.userName,
                            dob: '',
                            tob: '',
                            pob: '',
                            gender: 'Male',
                            notes: ''
                          });
                        }}
                        className="w-full py-3 bg-[#E8B84B]/10 border border-[#E8B84B]/25 text-[#E8B84B] font-bold text-[9px] uppercase tracking-wider rounded-xl hover:bg-[#E8B84B] hover:text-black transition-all"
                      >
                        ➕ Input Birth Details
                      </button>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-auto w-full pt-6 border-t border-white/5">
                <p className="text-[9px] text-white/20 italic mb-4 font-serif">
                  "Serving this seeker with divine truth and clarity."
                </p>
                <button className="w-full py-3 rounded-xl bg-[#2A2858]/10 border border-[#2A2858]/30 text-[#2A2858] text-xs font-bold hover:bg-[#2A2858] hover:text-white transition-all">
                  View Complete Profile
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-white/10 p-20 text-center bg-[#0E0E26]">
            <div className="bg-[#14122B] p-12 rounded-full border border-white/5 shadow-inner mb-8">
              <span className="material-symbols-outlined text-[100px] text-[#E8B84B]/20">psychology</span>
            </div>
            <h3 className="text-3xl font-black text-white/30 uppercase tracking-[0.3em] mb-4">Centre Oracle</h3>
            <p className="max-w-md text-white/20 text-xs font-bold uppercase tracking-widest leading-loose">
              Please select a sacred consultation from the left to begin the divine guidance session.
            </p>
          </div>
        )}
      </div>

      {/* Call UI Overlays */}
      {incomingCall && (
        <div className="fixed inset-0 z-[1000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="bg-[#14122B] border border-[#E8B84B]/30 rounded-3xl p-8 shadow-2xl flex flex-col items-center gap-6 min-w-[320px] animate-in fade-in zoom-in duration-300">
            <div className="w-20 h-20 rounded-full bg-[#2A2858] flex items-center justify-center animate-bounce shadow-xl border-4 border-white/5">
              <span className="material-symbols-outlined text-white text-4xl">
                {incomingCall.type === 'video' ? 'videocam' : 'call'}
              </span>
            </div>
            <div className="text-center">
              <p className="text-[#E8B84B] text-[10px] font-black uppercase tracking-[0.2em] mb-2">Incoming Divine Call</p>
              <h4 className="text-white font-bold text-2xl mb-1">{incomingCall.callerName || 'Devoted Seeker'}</h4>
              <p className="text-white/40 text-xs italic">Wishes to connect for {incomingCall.type} consultation</p>
            </div>
            <div className="flex gap-4 w-full mt-2">
              <button
                onClick={rejectCall}
                className="flex-1 py-4 rounded-2xl bg-red-500/10 text-red-500 font-bold hover:bg-red-500 hover:text-white transition-all border border-red-500/20 active:scale-95"
              >
                Decline
              </button>
              <button
                onClick={acceptCall}
                className="flex-1 py-4 rounded-2xl bg-green-500 text-white font-bold hover:bg-green-600 transition-all shadow-lg shadow-green-500/20 active:scale-95"
              >
                Accept Call
              </button>
            </div>
          </div>
        </div>
      )}

      {callActive && (
        <div className="fixed inset-0 z-[2000] bg-black flex flex-col animate-in fade-in duration-500">
          {/* Video Streams */}
          {callType === 'video' ? (
            <div className="relative flex-1 bg-stone-900 overflow-hidden">
              {/* Remote Video */}
              <video
                autoPlay
                playsInline
                muted
                ref={remoteVideoRef}
                className="w-full h-full object-cover"
              />

              {/* Local Video (PIP) */}
              <div className="absolute top-8 right-8 w-40 h-56 rounded-3xl overflow-hidden border-2 border-white/20 shadow-2xl bg-black group transition-all hover:scale-105">
                <video
                  autoPlay
                  muted
                  playsInline
                  ref={localVideoRef}
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="absolute bottom-12 left-0 right-0 flex justify-center items-center">
                <div className="px-6 py-3 rounded-full bg-black/40 backdrop-blur-xl text-white text-xs font-bold border border-white/10 flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-green-500' : 'bg-yellow-500'} animate-pulse`}></div>
                  {selectedSession?.userName} • {connectionStatus === 'connected' ? 'Live Consultation' : 'Connecting...'}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center bg-gradient-to-b from-[#0E0E26] to-[#000000] p-12">
              <div className="w-40 h-40 rounded-full bg-[#E8B84B]/5 border-2 border-[#E8B84B]/20 flex items-center justify-center mb-10 relative">
                <div className="w-32 h-32 rounded-full bg-stone-800 flex items-center justify-center overflow-hidden border border-[#E8B84B]/30">
                  <span className="material-symbols-outlined text-[#E8B84B] text-6xl">person</span>
                </div>
                <div className="absolute inset-0 rounded-full border-4 border-[#E8B84B] animate-ping opacity-10"></div>
              </div>
              <h3 className="text-white text-3xl font-serif font-bold mb-3 tracking-wide">{selectedSession?.userName}</h3>
              <div className="flex items-center gap-3">
                <span className={`w-2 h-2 rounded-full ${connectionStatus === 'connected' ? 'bg-[#E8B84B]' : 'bg-yellow-500'} animate-pulse`}></span>
                <p className="text-[#E8B84B] text-[11px] font-black uppercase tracking-[0.4em]">{connectionStatus === 'connected' ? 'Voice Consultation Active' : 'Connecting Audio...'}</p>
              </div>
            </div>
          )}

          {/* Call Controls */}
          <div className="h-36 shrink-0 bg-black/60 backdrop-blur-3xl border-t border-white/5 flex items-center justify-center gap-16 px-10">
            <button
              onClick={toggleMute}
              className={`w-16 h-16 rounded-full border transition-all hover:-translate-y-1 flex items-center justify-center ${isMuted
                ? 'bg-red-600 border-red-700 text-white'
                : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                }`}
              title={isMuted ? "Unmute Mic" : "Mute Mic"}
            >
              <span className="material-symbols-outlined text-2xl">{isMuted ? 'mic_off' : 'mic'}</span>
            </button>

            <button
              onClick={endCall}
              className="w-20 h-20 rounded-full bg-red-600 text-white flex items-center justify-center shadow-2xl shadow-red-600/30 hover:bg-red-700 transition-all hover:scale-110 active:scale-95"
            >
              <span className="material-symbols-outlined text-4xl">call_end</span>
            </button>

            <button
              onClick={toggleCamera}
              disabled={callType !== 'video'}
              className={`w-16 h-16 rounded-full border transition-all hover:-translate-y-1 flex items-center justify-center ${isCamOff
                ? 'bg-red-600 border-red-700 text-white'
                : 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                } ${callType !== 'video' ? 'opacity-30 cursor-not-allowed' : ''}`}
              title={isCamOff ? "Turn Camera On" : "Turn Camera Off"}
            >
              <span className="material-symbols-outlined text-2xl">{isCamOff ? 'videocam_off' : 'videocam'}</span>
            </button>
          </div>
        </div>
      )}}

      {toastMessage && (
        <div className="fixed top-6 right-6 z-[3000] min-w-[280px] p-4 rounded-2xl shadow-2xl backdrop-blur-md border animate-in fade-in slide-in-from-top-4 duration-300 flex items-center gap-3 bg-[#1c1917]/95 text-white border-[#E8B84B]/30">
          <span className={`material-symbols-outlined text-xl ${toastMessage.type === 'success' ? 'text-green-500' : toastMessage.type === 'error' ? 'text-red-500' : 'text-[#E8B84B]'
            }`}>
            {toastMessage.type === 'success' ? 'check_circle' : toastMessage.type === 'error' ? 'cancel' : 'info'}
          </span>
          <span className="text-xs font-bold tracking-wide">{toastMessage.text}</span>
        </div>
      )}
    </div>
  );
};

export default ConsultationInbox;
